
import { Vue, Component, Prop } from "vue-property-decorator";
import vToggleSwitch from "@/components/vToggleSwitch.vue";
import vIngredientsDisplayNames from "@/components/vIngredientsDisplayNames.vue";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import GeneralRepository from "@/utils/API/GeneralRepository";
import Language from "@/models/Language";
import vPreLoader from "@/components/vPreLoader.vue";
import vModal from "@/components/vModal.vue";
import { debounce } from "@/utils/Helpers";
import { mapState } from "vuex";
import IngredientDisplay from "../models/Ingredient/IngredientDisplay";
import { ExportLabelState } from "@/models/NutritionLabels/ExportLabelState";
import vUSContainsPercentOfForm from "./vUSContainsPercentOfForm.vue";
import { RecipeCompositionMeasurement } from "@/models/Recipes/RecipeCompositionMeasurement";
import { getIngredientDisplays } from "@/services/ExportLabel.service";
import { LabelTypeMetadata } from "@/models/NutritionLabels/LabelTypeMetadata";
import { CanadaFOPHighInMetadata } from "@/models/NutritionLabels/CanadaFOPHighInMetadata";
import vAccordion from "./vAccordion.vue";
import vLabelPreviewImage from "./vLabelPreviewImage.vue";
import vFrontOfPackageConfig from "./vFrontOfPackageConfig.vue";
import vBackOfPackageConfig from "./vBackOfPackageConfig.vue";
import vExportLabelButtons from "./vExportLabelButtons.vue";
import vCautionaryFOPConfig from "./vCautionaryFOPConfig.vue";
import { ExportLabelView } from "@/models/Cautionary/ExportLabelView";
import VRecipeNutritionLabelPrintout from "@/components/vRecipeNutritionLabelPrintout.vue";
import {PrintRecipeDetails} from "@/models/Recipes/PrintRecipeDetails";
import {RecipeIngredient} from "@/models/Recipes/RecipeIngredient";
import vCautionaryInputs from "@/components/vCautionaryInputs.vue";
import {RecipeLabel} from "@/models/NutritionLabels/RecipeLabel";
import {DishRecipeCommon} from "@/models/DishRecipeCommon";

@Component({
  components: {
    vCautionaryInputs,
    VRecipeNutritionLabelPrintout,
    vToggleSwitch,
    vPreLoader,
    vIngredientsDisplayNames,
    vModal,
    vUSContainsPercentOfForm,
    vAccordion,
    vLabelPreviewImage,
    vFrontOfPackageConfig,
    vBackOfPackageConfig,
    vExportLabelButtons,
    vCautionaryFOPConfig,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountStoreState: (state: any) => state.AccountModule,
    }),
  },
})
export default class vExportLabel extends Vue {
  ExportLabelView = ExportLabelView;
  localizationState: any;
  accountStoreState: any;
  languages: Language[] = [];
  // isFopDisplay: boolean = false;
  exportLabelState: ExportLabelState = new ExportLabelState();
  previewAbortController = new AbortController();
  activeView: ExportLabelView = ExportLabelView.BOP;
  LabelTypeMetadata = LabelTypeMetadata;
  isLabelLoading: boolean = false;

  fetchedLabel: RecipeLabel = null;

  @Prop() recipe?: DishRecipeCommon;
  @Prop() packageLabelType?: LabelTypeMetadata | LabelTypeMetadata.NutritionFacts;
  @Prop({default: (): RecipeCompositionMeasurement[] => [] }) subrecipeServingSizes: RecipeCompositionMeasurement[];
  @Prop() printMeta: PrintRecipeDetails;
  @Prop({ default: LabelTypeMetadata.NutritionFacts }) preselectedLabelType: LabelTypeMetadata;
  @Prop() highIn?: CanadaFOPHighInMetadata | CanadaFOPHighInMetadata.None;
  @Prop({ default: 0 }) roundingRuleIndex: number;
  @Prop({
    default: (): RecipeCompositionMeasurement[] => {
      return [];
    }
  }) subrecipeMeasurements!: RecipeCompositionMeasurement[];

  created() {
    this.fetchRecipeLabel();

    this.exportLabelState.nutritionCountry = this.recipe.nutritionCountry;

    GeneralRepository.listLanguages().then((response) => {
      this.languages = response.data.result;
    });

    // set IE mode
    /* MSIE used to detect old browsers and Trident used to newer ones*/
    this.exportLabelState.isIE = (
      navigator.userAgent.indexOf("MSIE ") > -1 ||
      navigator.userAgent.indexOf("Trident/") > -1 ||
      navigator.userAgent.indexOf("Edge") > -1
    );
  }

  async fetchRecipeLabel() {
    try {
      console.log("Begin fetch recipe label.");
      const response = await NutritionLabelRepository.getRecipeLabel(this.recipe.id);
      this.ingredientAndSubRecipeDisplaysInit();
      this.fetchedLabel = response.data || null;
      //await this.fetchPreview();

      console.log("End fetch recipe label.");
    }

    catch(e) {
      console.log("Error fetch recipe label.");
      console.error(e);
    }
  }

  ingredientAndSubRecipeDisplaysInit() {
    if(this.exportLabelState.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.length == 0){
      this.exportLabelState.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList = this.exportLabelState.getIngredientAndSubRecipeDisplays(
        this.recipe,
        this.subrecipeMeasurements,
        true
      );
    }

    if(this.exportLabelState.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.length == 0){
      this.exportLabelState.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList = this.exportLabelState.getIngredientAndSubRecipeDisplays(
        this.recipe,
        this.subrecipeMeasurements,
        false
      );
    }
  }

  print() {
    window.print();
  }

  /**
   * Gets a formatted list of ingredient displays
   * NOTE: This is for Recipes only
   */
  get ingredientDisplays(): IngredientDisplay[] {
    // If we're in a dish recipe, ingredients will be undefined, so we use an empty array
    let ingredientsList: RecipeIngredient[] = [];

    if (this.recipe.hasOwnProperty('ingredients')) {
      // Since this is checked, we know recipe has an ingredients property
      // @ts-ignore
      ingredientsList = this.recipe.ingredients;
    }
    return getIngredientDisplays(ingredientsList, this.exportLabelState.savedDisplays);
  }

  log(data: any) {
    console.log(data);
  }
  get shouldDisplayLabel(){
    return this.exportLabelState.nutritionCountry
      && this.exportLabelState.nutritionLabel !== null
      && this.exportLabelState.labelPreview != null;
  }

  saveRecipeLabel() {
    const labelTypes = {
      frontOfPack: this.exportLabelState.labelTypes[LabelTypeMetadata.FrontOfPack],
      cautionaryFrontOfPack: this.exportLabelState.labelTypes[LabelTypeMetadata.CautionaryFrontOfPack],
      cautionarySideOfPack: this.exportLabelState.labelTypes[LabelTypeMetadata.CautionarySideOfPack],
    };

    NutritionLabelRepository.saveRecipeLabel({
      recipeId: this.recipe.id,
      allergens: this.exportLabelState.allergens,
      nutritionCountryId: this.exportLabelState.nutritionCountry
        ? this.exportLabelState.nutritionCountry.id
        : 0,
      nutritionLabelId: this.exportLabelState.nutritionLabel ? this.exportLabelState.nutritionLabel.id : 0,
      primaryLanguage: this.exportLabelState.primaryLanguage ? this.exportLabelState.primaryLanguage.value : "",
      secondaryLanguage: this.exportLabelState.secondaryLanguage
        ? this.exportLabelState.secondaryLanguage.value
        : "",
      dietaryTypes: this.exportLabelState.dietary,
      ingredientsIncluded: this.exportLabelState.includeIngredients,
      additionalContents: this.exportLabelState.mayContain,
      cautionaryMessage: this.exportLabelState.cautionaryMessage,
      headerLabelTranslations: this.exportLabelState.headerLabelTranslations,
      freeTextPlaceholders: this.exportLabelState.freeTextPlaceholders,
      containsLessThanPercentOfContent: this.exportLabelState.containsLessThanPercentOf,
      ingredientDisplayModels: this.exportLabelState.renderExpandedSubrecipes
        ? this.exportLabelState.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList
        : this.exportLabelState.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList,
      ingredientOtherDisplayModel: !this.exportLabelState.renderExpandedSubrecipes
        ? this.exportLabelState.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList
        : this.exportLabelState.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList,
      subRecipeDisplayModels: this.exportLabelState.getSubRecipeDisplays(this.recipe, this.subrecipeMeasurements),
      renderExpandedSubrecipes: this.exportLabelState.renderExpandedSubrecipes,
      labelTypes: labelTypes
    }).then(() => {
      this.exportLabelState.textPlaceholdersOfSavedLabel =
        this.exportLabelState.freeTextPlaceholders; // Change to newly saved label
      this.$snotify.success(this.translations.get("labels_save_success"));
    });
  }

  fetchPreview() {
    if (this.exportLabelState.nutritionCountry && this.exportLabelState.nutritionLabel && this.exportLabelState.primaryLanguage) {
      if (this.exportLabelState.nutritionCountry.name == "UK") {
        this.exportLabelState.headerLabelTranslations.ingredientsSecondaryLanguageTranslation =
          "";
        this.ingredientDisplays.map((ingredient) => {
          ingredient.displayTextInSecondaryLanguage = "";
        });
      }

      this.exportLabelState.disableDownload = true;
      this.exportLabelState.loading = true;

      NutritionLabelRepository.previewNutritionLabelForRecipe({
        recipeId: this.recipe.id,
        allergens: this.exportLabelState.checkedAllergens,
        nutritionLabelId: this.exportLabelState.nutritionLabel ? this.exportLabelState.nutritionLabel.id : 0,
        primaryLanguage: this.exportLabelState.primaryLanguage ? this.exportLabelState.primaryLanguage.value : "",
        secondaryLanguage: this.exportLabelState.secondaryLanguage ? this.exportLabelState.secondaryLanguage.value : "",
        dietaryTypes: this.exportLabelState.dietary,
        ingredientsIncluded: this.exportLabelState.includeIngredients,
        additionalContent: this.exportLabelState.mayContain,
        cautionaryMessage: this.exportLabelState.cautionaryMessage,
        containsLessThanPercentOfText: this.exportLabelState.containsLessThanPercentOf,
        headerTranslations: this.exportLabelState.headerLabelTranslations,
        nutritionLabelDownloadFileFormat: 0,
        labelWidth: 0,
        labelHeight: 0,
        freeTextPlaceholders: this.exportLabelState.freeTextPlaceholders,
        ingredientDisplayModels: this.exportLabelState.renderExpandedSubrecipes
          ? this.exportLabelState.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList
          : this.exportLabelState.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList,
      })
        .then((response) => {
          this.exportLabelState.labelPreview = response.data.result;
          this.exportLabelState.loading = false;
          this.exportLabelState.disableDownload = false;
        });
    }
  }

  loadFOPLabelCreator() {
    this.activeView = ExportLabelView.FOP;
  }

  loadBOPLabelCreator() {
    this.activeView = ExportLabelView.BOP;
  }

  loadCautionaryFOPLabelCreator() {
    this.activeView = ExportLabelView.CautionaryFOP;
  }

  loadCautionaryBOPLabelCreator() {
    this.activeView = ExportLabelView.CautionaryBOP;
  }

  debouncedFetchPreview() {
    this.previewAbortController.abort("Refetch");
    debounce(this.fetchPreview, 1000, "export-label-fetch-preview");
  }

  handleSwitchView(nextView: ExportLabelView) {
    this.activeView = nextView;
  }

  handleGoBack() {
    switch (this.activeView) {
    case ExportLabelView.FOP:
      this.activeView = ExportLabelView.BOP;
      break;
    case ExportLabelView.CautionaryBOP:
      this.activeView = ExportLabelView.FOP;
      break;
    case ExportLabelView.CautionaryFOP:
      this.activeView = ExportLabelView.CautionaryBOP;
      break;
    }
  }

  get translations() {
    return this.localizationState.translations;
  }
}
